import { useState } from 'react'
import { Loader2, MoreHorizontal, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useDeleteMessage, extractErrorMessage } from '@/queries/ticketMutations'
import type { TicketMessage } from '@/types/ticket'

/** Offered on a staff member's own replies and notes only; the server enforces the same rule. */
export function DeleteMessageMenu({ ticketId, message }: { ticketId: string; message: TicketMessage }) {
  const remove = useDeleteMessage(ticketId)
  const [confirming, setConfirming] = useState(false)

  return (
    <>
      {/* Non-modal so the menu releases focus and pointer events to the dialog it opens. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-xs" className="ml-auto" aria-label="Message actions">
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setConfirming(true)}>
            <Trash2 />
            Delete {message.isInternal ? 'note' : 'reply'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this {message.isInternal ? 'note' : 'reply'}?</DialogTitle>
            <DialogDescription>
              {message.isInternal
                ? 'It will show as deleted in the thread. Staff can still read the original.'
                : 'The customer may already have been emailed this reply: deleting it removes it from the thread, not from their inbox. Staff can still read the original.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" disabled={remove.isPending} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={async () => {
                try {
                  await remove.mutateAsync(message._id)
                  setConfirming(false)
                } catch (err) {
                  toast.error(extractErrorMessage(err, 'Could not delete the message'))
                }
              }}
            >
              {remove.isPending ? <Loader2 className="animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
